import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFilter, faTimes } from '@fortawesome/free-solid-svg-icons';

const GlossarySearch = ({ 
  searchTerm, 
  onSearch, 
  selectedFilter, 
  onFilterClick, 
  theme 
}) => {
  const styles = {
    container: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    searchWrapper: {
      flex: 1,
      position: 'relative',
    },
    searchInput: {
      width: '100%',
      padding: '1rem 3rem',
      borderRadius: '0.75rem',
      border: `1px solid ${theme?.borders?.primary}`,
      background: theme?.background?.card,
      fontSize: '0.875rem',
      color: theme?.text?.primary,
      outline: 'none',
      transition: 'all 0.3s ease',
    },
    searchInputFocus: {
      borderColor: theme?.primary?.blue?.[500],
      boxShadow: `0 0 0 3px ${theme?.primary?.blue?.[500]}20`,
    },
    searchIcon: {
      position: 'absolute',
      left: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: theme?.text?.tertiary,
      fontSize: '1rem',
    },
    clearButton: {
      position: 'absolute',
      right: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      color: theme?.text?.tertiary,
      cursor: 'pointer',
      padding: '0.25rem',
      borderRadius: '0.25rem',
      transition: 'all 0.3s ease',
      opacity: searchTerm ? 1 : 0,
      pointerEvents: searchTerm ? 'auto' : 'none',
    },
    clearButtonHover: {
      color: theme?.text?.primary,
      background: theme?.backgrounds?.secondary,
    },
    filterButton: {
      padding: '1rem 1.25rem',
      borderRadius: '0.75rem',
      border: `1px solid ${theme?.borders?.primary}`,
      background: theme?.background?.card,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      position: 'relative',
    },
    filterButtonActive: {
      background: theme?.primary?.blue?.[100],
      borderColor: theme?.primary?.blue?.[300],
    },
    filterIcon: {
      fontSize: '1rem',
      color: theme?.text?.secondary,
    },
    filterBadge: {
      position: 'absolute',
      top: '-0.25rem',
      right: '-0.25rem',
      background: theme?.primary?.blue?.[500],
      color: 'white',
      fontSize: '0.625rem',
      fontWeight: '600',
      padding: '0.125rem 0.375rem',
      borderRadius: '0.625rem',
      minWidth: '1.25rem',
      textAlign: 'center',
    },
  };

  const handleInputChange = (e) => {
    onSearch(e.target.value);
  };

  const handleClear = () => {
    onSearch('');
  };

  const isFilterActive = selectedFilter !== 'ALL';

  return (
    <div style={styles.container}>
      <div style={styles.searchWrapper}>
        <FontAwesomeIcon icon={faSearch} style={styles.searchIcon} />
        
        <motion.input
          type="text"
          placeholder="Search terms or definitions..."
          value={searchTerm}
          onChange={handleInputChange}
          style={styles.searchInput}
          whileFocus={styles.searchInputFocus}
          initial={{ scale: 0.98 }}
          animate={{ scale: 1 }}
        />
        
        <motion.button
          style={styles.clearButton}
          whileHover={styles.clearButtonHover}
          whileTap={{ scale: 0.9 }}
          onClick={handleClear}
        >
          <FontAwesomeIcon icon={faTimes} />
        </motion.button>
      </div>
      
      <motion.button
        style={{
          ...styles.filterButton,
          ...(isFilterActive && styles.filterButtonActive),
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={onFilterClick}
      >
        <FontAwesomeIcon 
          icon={faFilter} 
          style={{
            ...styles.filterIcon,
            color: isFilterActive ? theme?.primary?.blue?.[600] : theme?.text?.secondary,
          }}
        />
        <span style={{ 
          color: isFilterActive ? theme?.primary?.blue?.[600] : theme?.text?.primary,
          fontWeight: isFilterActive ? '500' : '400',
        }}>
          {selectedFilter === 'ALL' ? 'Filter' : selectedFilter}
        </span>
        
        {isFilterActive && (
          <div style={styles.filterBadge}>1</div>
        )}
      </motion.button>
    </div>
  );
};

export default GlossarySearch;